import { useParams, useSearchParams } from "react-router-dom";

import { useGetContactEditSession, type ContactEditSessionInfo } from "@wellcheck/api-client";

import { isContactEditClosedError } from "../api/contactEditClosed";

/**
 * useContactEditSession — 連絡先修正セッションを確認する。
 *
 * サーバ側 (backend/handlers/contactedit_verify.go, ADR-0041 Decision 4) が `?t=<token>` を
 * 検証し、成功なら HttpOnly セッション Cookie (__Host-wc_contact_edit_session) を発行した上で
 * `t` を除いた本 URL へ 303 リダイレクトする。そのため本 hook は URL から token を扱わず、
 * Cookie の有効性を `GET /api/contact-edit-session` に問い合わせるだけでよい
 * (features/respond/hooks/useRespondSession.ts と同じ設計)。
 *
 * 修正受付期限超過 (ADR-0041 Decision 5-1) は 2 経路で表れる。verify 時点で期限切れなら handler が
 * Cookie を発行せず `?reason=contact_edit_closed` 付きで redirect する
 * (contactEditClosedRedirectQuery)。この場合は終端状態が URL だけで確定し Cookie も無いため、
 * `enabled` を落として結果を捨てるだけの無駄なリクエストを避ける (useRespondSession と同じ)。
 * verify 通過後に Cookie の TTL (60 分) 内で日付を跨いだ場合は session API が 409 を返すため、
 * そちらも "invalid" (壊れたリンク・token 期限切れ) と区別して "closed" に倒す。
 *
 * 未受検 (abandoned) にされた respondent が verify handler に到達した場合も、同じ
 * 「Cookie 未発行 + reason クエリ」の仕組みで `?reason=respondent_abandoned` として表れる
 * (ADR-0054 Decision 3、features/respond の useRespondSession と同じ扱い)。
 *
 * campaignRecordId/respondentRecordId は session API のレスポンスの ID と突合する (ADR-0023
 * 追記)。別回答者の有効な Cookie が残っていても別人の修正画面を表示しないため。
 */
export interface ContactEditSessionParams {
  campaignRecordId: string;
  respondentRecordId: string;
}

export type ContactEditSessionState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "closed" }
  | { status: "abandoned" }
  | { status: "valid"; session: ContactEditSessionInfo };

export function useContactEditSession(): ContactEditSessionParams & {
  session: ContactEditSessionState;
} {
  const { campaignRecordId, respondentRecordId } = useParams<
    "campaignRecordId" | "respondentRecordId"
  >();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get("reason");
  const isClosed = reason === "contact_edit_closed";
  const isAbandoned = reason === "respondent_abandoned";

  const query = useGetContactEditSession({
    query: {
      // 401 はリトライ対象外 (無効セッションを再試行しても状態は変わらない)。
      retry: false,
      select: (response) => (response.status === 200 ? response.data : undefined),
      enabled: !isClosed && !isAbandoned,
    },
  });

  const session: ContactEditSessionState = isClosed
    ? { status: "closed" }
    : isAbandoned
      ? { status: "abandoned" }
      : query.isLoading
        ? { status: "loading" }
        : isContactEditClosedError(query.error)
          ? { status: "closed" }
          : query.isError || !query.data
            ? { status: "invalid" }
            : query.data.campaignRecordId !== campaignRecordId ||
                query.data.respondentRecordId !== respondentRecordId
              ? { status: "invalid" }
              : { status: "valid", session: query.data };

  return {
    campaignRecordId: campaignRecordId ?? "",
    respondentRecordId: respondentRecordId ?? "",
    session,
  };
}
